'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { UserCog, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEndImpersonation } from '@/lib/admin/hooks/use-impersonation';
import { markImpersonationExiting } from '@/lib/api/impersonation-exit';

interface ImpersonationMeta {
  targetEmail: string;
  companyId?: string;
  workspaceId?: string | null;
  startedAt: string;
  expiresAt: string;
}

export function GlobalImpersonationBanner() {
  const [meta, setMeta] = useState<ImpersonationMeta | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const endImpersonation = useEndImpersonation();
  const pathname = usePathname();

  // Read flag + meta on mount AND on every navigation. The root layout
  // persists across client-side navigations, so a one-shot useEffect with
  // [] dependencies only runs ONCE — at the URL where the tab first opens.
  // The exchange page sets sessionStorage AFTER the layout has mounted,
  // then router.replace()s into the customer shell. Without `pathname` in
  // the deps, the banner would only see the empty sessionStorage from the
  // first mount and never re-check post-redirect.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('hf-app:is-impersonating') !== 'true') {
      setMeta(null);
      return;
    }
    const raw = sessionStorage.getItem('hf-app:impersonation-meta');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as ImpersonationMeta;
      setMeta(parsed);
    } catch {
      // Corrupted JSON — bail silently
    }
  }, [pathname]);

  // Countdown timer — auto-exits when TTL reaches zero
  useEffect(() => {
    if (!meta) return;
    const tick = () => {
      const left = new Date(meta.expiresAt).getTime() - Date.now();
      setRemainingMs(Math.max(0, left));
      if (left <= 0) {
        void exit();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  async function exit() {
    // Open the suppression window BEFORE the call. /end now revokes the
    // impersonation session server-side rather than only writing an audit row
    // (RC-F), so from the moment it returns every in-flight query 401s.
    // lib/api/client.ts reads this window and suppresses its force-logout,
    // which would otherwise redirect the super-admin to the CUSTOMER login page
    // instead of /admin.
    markImpersonationExiting();
    try {
      await endImpersonation.mutateAsync();
    } catch {
      // Never block exit on failure: the session is also capped server-side, so
      // a failed call delays the kill rather than losing it.
    }
    // Clear all hf-app:* localStorage keys written during exchange
    const keys = [
      'hf-app:access-token',
      'hf-app:refresh-token',
      'hf-app:workspace-id',
      'hf-app:company-id',
    ];
    keys.forEach((k) => localStorage.removeItem(k));
    sessionStorage.removeItem('hf-app:is-impersonating');
    sessionStorage.removeItem('hf-app:impersonation-meta');
    // Clear cookies that mirror localStorage
    document.cookie =
      'hf-app:access-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie =
      'hf-app:workspace-id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    if (typeof window !== 'undefined') {
      if (window.opener && !window.opener.closed) window.close();
      else window.location.href = '/admin';
    }
    // NOT cleared here. Assigning `window.location.href` does not stop this
    // function, so a removal on this line would run immediately and leave the
    // suppression window zero-width — the race it exists for would still be
    // lost. The window carries its own deadline
    // (IMPERSONATION_EXIT_WINDOW_MS) and closes itself, so a genuine session
    // expiry in this tab afterwards still redirects to login.
  }

  if (!meta) return null;

  const mm = Math.floor(remainingMs / 60000)
    .toString()
    .padStart(2, '0');
  const ss = Math.floor((remainingMs % 60000) / 1000)
    .toString()
    .padStart(2, '0');

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 w-full border-b border-amber-500/30 bg-amber-100 text-amber-950 dark:border-amber-400/30 dark:bg-amber-950/60 dark:text-amber-100"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-3 px-4 py-2 sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <UserCog className="h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="truncate text-sm">
            <span className="font-semibold">
              Impersonating {meta.targetEmail}
            </span>
            <span className="opacity-80">
              {' '}
              · expires in {mm}:{ss}
            </span>
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void exit()}
          className="h-7 gap-1.5 border-amber-500/40 bg-transparent hover:bg-amber-200/60 dark:hover:bg-amber-900/60"
          aria-label="Exit impersonation"
        >
          <X className="h-3.5 w-3.5" />
          Exit
        </Button>
      </div>
    </div>
  );
}
